/**
 * Main-thread client for the image processing worker.
 *
 * Responsibilities:
 *  - own the worker lifecycle and reuse it across requests,
 *  - correlate request/response pairs by id,
 *  - stream progress updates back so the UI can show what is happening,
 *  - fall back to running the same pipeline inline if workers are unavailable,
 *    so the tool still works (just blocking) rather than failing outright.
 *
 * The fallback reuses `runPipeline` from `pipeline.ts`, the exact code the worker
 * runs, so the two paths cannot drift.
 */

import { runPipeline, hasMaskWork, type PipelineResult, type PipelineStage } from './pipeline';
import type { WorkerRequest, WorkerResponse, WarmRequest } from './inpaint.worker';
import type { InpaintMethod } from './inpaint';

// Imported for its side effect: registers the main-thread OpenCV provider.
// Without this the inline fallback has no way to obtain the engine and fails
// with "No OpenCV provider registered" — which is precisely what happens
// whenever the worker cannot start.
import './openCvRuntime';


/** Built by scripts/build-worker.ts; served verbatim by both dev and production. */
const WORKER_URL = '/vendor/inpaint-worker.js';
/** Settings for a run. The mask travels separately, in \ProcessInput\. */
export interface ProcessOptions {
  scaleFactor: number;
  sharpenAmount: number;
  inpaintRadius: number;
  inpaintMethod: InpaintMethod;
  onStage?: (stage: PipelineStage) => void;
  /** Force the inline path. Used by tests and as an escape hatch. */
  forceMainThread?: boolean;
  /** Print resolution to stamp into the PNG when encoding in the worker. */
  targetDpi?: number;
}

let worker: Worker | null = null;
let workerUnavailable = false;
let nextId = 1;

export interface ProcessedImage {
  /** Compressed PNG bytes when the worker could encode; otherwise null. */
  png: ArrayBuffer | null;
  /** Raw pixels, only used when `png` is null. */
  result: PipelineResult;
}

interface Pending {
  resolve: (image: ProcessedImage) => void;
  reject: (error: Error) => void;
  onStage?: (stage: PipelineStage) => void;
}

const pending = new Map<number, Pending>();

/** Whether the worker path is in use. Exposed for the UI's status text. */
export function isUsingWorker(): boolean {
  return worker !== null && !workerUnavailable;
}

function ensureWorker(): Worker | null {
  if (workerUnavailable) return null;
  if (worker) return worker;

  if (typeof Worker === 'undefined') {
    workerUnavailable = true;
    return null;
  }

  try {
// A plain URL to a pre-bundled classic worker, built by
    // scripts/build-worker.ts on predev/prebuild. Deliberately not routed
    // through Vite: its dev-time handling of classic workers serves unbundled
    // ESM, which only breaks in development. The worker must also stay
    // classic -- emscripten detects a worker environment via `importScripts`
    // and assigns itself with `root.cv = ...`, where `root` is the top-level
    // `this` (undefined in a module worker).
    worker = new Worker(WORKER_URL);
    worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
      const message = event.data;
      const entry = pending.get(message.id);
      if (!entry) return;

      if (message.type === 'progress') {
        entry.onStage?.(message.stage);
        return;
      }
      pending.delete(message.id);
      if (message.type === 'result') {
        entry.resolve({ png: message.png, result: message.result });
      } else if (message.type === 'warm-done') {
        // Warming carries no pixels; the caller only cares that it finished.
        entry.resolve({ png: null, result: { data: new Uint8ClampedArray(0), width: 0, height: 0 } });
      } else {
        entry.reject(new Error(message.message));
      }
    });
    worker.addEventListener('error', (event) => {
      // A worker-level failure (bad module, blocked importScripts) invalidates
      // every outstanding request. Fail them over to the inline path.
      const message = event.message || 'The background worker failed to start.';
      const entries = [...pending.entries()];
      pending.clear();
      worker?.terminate();
      worker = null;
      workerUnavailable = true;
      for (const [, entry] of entries) entry.reject(new Error(message));
    });
  } catch {
    workerUnavailable = true;
    worker = null;
  }

  return worker;
}

export interface ProcessInput {
  data: Uint8ClampedArray;
  width: number;
  height: number;
  mask: Uint8Array;
}

/**
 * Run the full pipeline, in a worker when possible.
 *
 * Falls back to the main thread automatically on any worker failure, so callers
 * do not need to care which path was taken.
 */
export async function processImage(
  input: ProcessInput,
  options: ProcessOptions
): Promise<ProcessedImage> {
  const useWorker = !options.forceMainThread && !workerUnavailable;
  if (useWorker) {
    const active = ensureWorker();
    if (active) {
      try {
        return await sendToWorker(active, input, options);
      } catch (error) {
        // A deliberate shutdown (unmount) is not a worker failure: don't redo the
        // work on the main thread or mark the worker unusable for the session.
        if (error instanceof WorkerDisposedError) throw error;
        // Fall through to the inline path: a dropped worker should degrade
        // performance, not break the feature.
        console.warn('[image-upscaler] worker failed, falling back to main thread:', error);
        workerUnavailable = true;
      }
    }
  }

  const result = await runPipeline({
    data: input.data,
    width: input.width,
    height: input.height,
    options: { ...options, mask: input.mask },
    onStage: options.onStage,
  });
  return { png: null, result };
}

function sendToWorker(
  active: Worker,
  input: ProcessInput,
  options: ProcessOptions
): Promise<ProcessedImage> {
  const id = nextId++;

  // The source pixels are copied rather than transferred: the caller still needs
  // them (they came from a canvas that stays on screen).
  const copy = new Uint8ClampedArray(input.data);
  const request: WorkerRequest = {
    id,
    data: copy.buffer as ArrayBuffer,
    width: input.width,
    height: input.height,
    options: {
      mask: input.mask,
      scaleFactor: options.scaleFactor,
      sharpenAmount: options.sharpenAmount,
      inpaintRadius: options.inpaintRadius,
      inpaintMethod: options.inpaintMethod,
    },
    removeWatermark: hasMaskWork(input.mask),
    targetDpi: options.targetDpi ?? 0,
  };

  return new Promise<ProcessedImage>((resolve, reject) => {
    pending.set(id, { resolve, reject, onStage: options.onStage });
    try {
      active.postMessage(request, [request.data]);
    } catch (error) {
      pending.delete(id);
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });
}

/**
 * Start loading the OpenCV engine in the worker ahead of time.
 *
 * The bundle is ~10 MB, so this overlaps the download and WASM compile with the
 * user deciding what to remove instead of stalling their click. Resolves either
 * way; failures are reported through the normal error path.
 */
export async function warmEngine(): Promise<boolean> {
  const active = ensureWorker();
  if (!active) return false;

  const id = nextId++;
  return new Promise<boolean>((resolve) => {
    pending.set(id, {
      resolve: () => resolve(true),
      reject: () => resolve(false),
    });
    try {
      active.postMessage({ id, type: 'warm' } satisfies WarmRequest);
    } catch {
      pending.delete(id);
      resolve(false);
    }
  });
}
/** Rejection reason for requests still in flight when the worker is disposed. */
export class WorkerDisposedError extends Error {
  constructor() {
    super('The background worker was shut down.');
    this.name = 'WorkerDisposedError';
  }
}

/** Tear the worker down. Used on unmount and by tests. */
export function disposeWorker(): void {
  // Reject rather than drop: a cleared-but-unsettled promise leaves its caller
  // hanging forever.
  const entries = [...pending.values()];
  pending.clear();
  for (const entry of entries) entry.reject(new WorkerDisposedError());
  worker?.terminate();
  worker = null;
  workerUnavailable = false;
}
